import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, Package, Star, Eye, ArrowLeft, Trash2 } from 'lucide-react';
import { favoritesAPI, photoAPI } from '../lib/api';
import { useFavorites } from '../context/FavoritesContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const condLabel = (s) => s >= 10 ? 'Neuf' : s >= 9 ? 'Neuf' : s >= 7 ? 'Très bon' : s >= 5 ? 'Bon état' : 'Satisfaisant';
const condColor = (s) => s >= 10 ? 'bg-emerald-500' : s >= 9 ? 'bg-green-500' : s >= 7 ? 'bg-sky-500' : s >= 5 ? 'bg-amber-500' : 'bg-orange-500';

const FavoritesPage = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toggleFavorite } = useFavorites();
  const navigate = useNavigate();

  useEffect(() => {
    favoritesAPI.getAll()
      .then(res => setListings(res.data.favorites || []))
      .catch(() => toast.error("Erreur lors du chargement des favoris"))
      .finally(() => setLoading(false));
  }, []);

  const handleRemove = async (e, listingId) => {
    e.preventDefault();
    e.stopPropagation();
    await toggleFavorite(listingId);
    setListings(prev => prev.filter(l => l.id !== listingId));
    toast.success("Retiré des favoris");
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7FAFB]">
      <div className="w-12 h-12 border-4 border-[#0C63BF] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F7FAFB]">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(-1)} className="text-[#5A8BA8] hover:text-[#0C63BF] transition-colors" data-testid="favorites-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-red-500 fill-red-500" />
            <h1 className="text-xl sm:text-2xl font-black text-[#033871]" data-testid="favorites-title">Mes favoris</h1>
          </div>
          <span className="text-sm text-[#5A8BA8] font-medium">({listings.length})</span>
        </div>

        {listings.length === 0 ? (
          <div className="text-center py-16" data-testid="favorites-empty">
            <Heart className="w-16 h-16 text-[#E8F4FD] mx-auto mb-4" />
            <h2 className="text-lg font-bold text-[#033871] mb-2">Aucun favori pour l'instant</h2>
            <p className="text-sm text-[#5A8BA8] mb-6">Ajoutez des raquettes à vos favoris pour les retrouver facilement !</p>
            <Link to="/catalogue">
              <Button className="bg-[#0C63BF] hover:bg-[#033871] rounded-xl font-bold" data-testid="favorites-browse-btn">
                Parcourir le catalogue
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4" data-testid="favorites-grid">
            {listings.map(listing => {
              const photoUrl = listing.photos?.[0] ? photoAPI.getUrl(listing.photos[0]) : null;
              const modelName = listing.model.replace(new RegExp('^' + listing.brand + '\\s+', 'i'), '');
              return (
                <Link to={`/annonce/${listing.id}`} key={listing.id} className="group" data-testid={`fav-card-${listing.id}`}>
                  <div className="bg-white rounded-2xl border border-[#E8F4FD] overflow-hidden hover:shadow-xl hover:border-transparent transition-all duration-300 hover:-translate-y-1">
                    <div className="aspect-[4/3] bg-[#F0F7FA] relative overflow-hidden">
                      {photoUrl ? (
                        <img src={photoUrl} alt={modelName} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#B8D4E3]">
                          <Package className="w-12 h-12" />
                        </div>
                      )}
                      <span className={`absolute top-2.5 left-2.5 px-2 py-0.5 rounded-lg text-[10px] font-bold text-white ${condColor(listing.condition_score)} shadow-sm`}>
                        {condLabel(listing.condition_score)}
                      </span>
                      <button onClick={(e) => handleRemove(e, listing.id)}
                        className="absolute top-2.5 right-2.5 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md hover:bg-red-50 transition-colors"
                        data-testid={`fav-remove-${listing.id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </button>
                    </div>
                    <div className="p-3.5">
                      <p className="text-[10px] text-[#5A8BA8] uppercase tracking-wider font-semibold">{listing.brand}</p>
                      <h3 className="font-bold text-[#033871] text-sm mt-0.5 line-clamp-1 group-hover:text-[#0C63BF] transition-colors">{modelName}</h3>
                      <div className="flex items-baseline gap-2 mt-2">
                        <span className="text-lg font-black text-[#0C63BF]">{listing.price.toFixed(0)}&euro;</span>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoritesPage;
